import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown } from "react-icons/fa";

interface ClickStyleDropdownProps {
  clickStyle: string;
  onClickStyleChange: (style: string) => void;
}

export const ClickStyleDropdown: React.FC<ClickStyleDropdownProps> = ({
  clickStyle,
  onClickStyleChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [enterTimeoutId, setEnterTimeoutId] = useState<NodeJS.Timeout | null>(
    null
  );
  const [leaveTimeoutId, setLeaveTimeoutId] = useState<NodeJS.Timeout | null>(
    null
  );

  const clickStyles = ["Quartz", "Orb", "Pulsar", "Classic", "Drop"];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (enterTimeoutId) clearTimeout(enterTimeoutId);
      if (leaveTimeoutId) clearTimeout(leaveTimeoutId);
    };
  }, [enterTimeoutId, leaveTimeoutId]);

  const handleStyleSelect = (style: string) => {
    onClickStyleChange(style);
    setIsOpen(false);
    setShowTooltip(false); // Hide tooltip when dropdown closes

    // Clear any pending timeouts to prevent tooltip from reappearing
    if (enterTimeoutId) {
      clearTimeout(enterTimeoutId);
      setEnterTimeoutId(null);
    }
    if (leaveTimeoutId) {
      clearTimeout(leaveTimeoutId);
      setLeaveTimeoutId(null);
    }
  };

  const handleMouseEnter = () => {
    // Clear any pending leave timeout
    if (leaveTimeoutId) {
      clearTimeout(leaveTimeoutId);
      setLeaveTimeoutId(null);
    }

    // Set enter timeout
    const id = setTimeout(() => {
      setShowTooltip(true);
    }, 500);
    setEnterTimeoutId(id);
  };

  const handleMouseLeave = () => {
    // Clear any pending enter timeout
    if (enterTimeoutId) {
      clearTimeout(enterTimeoutId);
      setEnterTimeoutId(null);
    }

    // Set leave timeout for smooth transition
    const id = setTimeout(() => {
      setShowTooltip(false);
    }, 100);
    setLeaveTimeoutId(id);
  };

  return (
    <div
      ref={dropdownRef}
      style={{ position: "relative", display: "block", width: "100%" }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: "12px 24px",
          fontSize: "16px",
          background: "var(--toggle-bg)",
          color: "var(--text-primary)",
          border: "none",
          borderRadius: "12px",
          cursor: "pointer",
          fontWeight: "500",
          WebkitBackdropFilter: "blur(7px)",
          backdropFilter: "blur(7px)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s ease",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          width: "100%",
          justifyContent: "center",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 12px 40px rgba(0, 0, 0, 0.2)";
          e.currentTarget.style.background = "var(--button-hover-bg)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 0, 0, 0.1)";
          e.currentTarget.style.background = "var(--toggle-bg)";
        }}
      >
        <span>{clickStyle}</span>
        {FaChevronDown({
          size: 8,
          style: {
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          },
        })}
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: "0",
            right: "0",
            background: "var(--toggle-bg)",
            border: "none",
            borderRadius: "12px",
            WebkitBackdropFilter: "blur(7px)",
            backdropFilter: "blur(7px)",
            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.3)",
            zIndex: 1000,
            overflow: "hidden",
          }}
        >
          {clickStyles.map((style) => (
            <button
              key={style}
              onClick={() => handleStyleSelect(style)}
              style={{
                width: "100%",
                padding: "12px 24px",
                fontSize: "16px",
                background:
                  style === clickStyle
                    ? "var(--button-hover-bg)"
                    : "transparent",
                color: "var(--text-primary)",
                border: "none",
                cursor: "pointer",
                fontWeight: "500",
                textAlign: "center",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (style !== clickStyle) {
                  e.currentTarget.style.background = "var(--button-bg)";
                }
              }}
              onMouseLeave={(e) => {
                if (style !== clickStyle) {
                  e.currentTarget.style.background = "transparent";
                }
              }}
            >
              {style}
            </button>
          ))}
        </div>
      )}

      {/* Tooltip */}
      <div
        style={{
          position: "absolute",
          bottom: "calc(100% + 8px)",
          left: "50%",
          transform: `translateX(-50%) ${
            showTooltip ? "translateY(0)" : "translateY(+10px)"
          }`,
          opacity: showTooltip ? 1 : 0,
          padding: "8px 12px",
          background: "var(--toggle-bg)",
          color: "var(--text-secondary)",
          borderRadius: "12px",
          fontSize: "clamp(11px, 3vw, 14px)",
          fontWeight: "normal",
          textAlign: "center",
          whiteSpace: "nowrap",
          zIndex: 1000,
          border: "none",
          backdropFilter: "blur(7px)",
          WebkitBackdropFilter: "blur(7px)",
          lineHeight: "1.4",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          transformOrigin: "center bottom",
          pointerEvents: "none",
          userSelect: "none",
        }}
      >
        Choose a metronome click style
      </div>
    </div>
  );
};
